/**
 * Public client-facing playlist manager.
 *
 * Flow:
 *  1. Visitor enters their activation key OR MAC address, clicks Look up.
 *  2. Backend returns their key status + attached playlists.
 *  3. If expired → prominent Renew button routes them to /activation.
 *  4. If active → list of playlists with Edit / Delete actions
 *     plus "Add another playlist" when under the cap.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  KeyRound,
  ListVideo,
  Search,
  Pencil,
  Trash2,
  Plus,
  Loader2,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  Clock,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Same MAC formatter used on /trial — mirrored here so a client typing
// their MAC in this page gets the same auto-colonize behaviour.
function formatMac(raw) {
  const hex = (raw || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase().slice(0, 12);
  return hex.match(/.{1,2}/g)?.join(":") || "";
}

function buildXtreamUrls({ host, port, username, password }) {
  let h = (host || "").trim().replace(/\/+$/, "");
  if (!h) return { m3u_url: "", epg_url: "" };
  if (!/^https?:\/\//i.test(h)) h = `http://${h}`;
  const hasPortInHost = /:\d+$/.test(h.replace(/^https?:\/\//i, ""));
  const portStr = String(port || "").trim();
  if (portStr && !hasPortInHost) h = `${h}:${portStr}`;
  const u = encodeURIComponent(username || "");
  const p = encodeURIComponent(password || "");
  return {
    m3u_url: `${h}/get.php?username=${u}&password=${p}&type=m3u_plus&output=m3u8`,
    epg_url: `${h}/xmltv.php?username=${u}&password=${p}`,
  };
}

const emptyForm = {
  name: "",
  host: "",
  port: "",
  username: "",
  password: "",
  m3u_url: "",
  epg_url: "",
};

export default function Playlist() {
  const [lookupMode, setLookupMode] = useState("key"); // "key" | "mac"
  const [lookupKey, setLookupKey] = useState("");
  const [lookupMac, setLookupMac] = useState("");
  const [busy, setBusy] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [session, setSession] = useState(null); // { activation_key, is_trial, is_expired, days_remaining, max_playlists, playlists[] }

  // Dialog state (add or edit)
  const [editing, setEditing] = useState(null); // null | { playlist_id?, ... }
  const [editSource, setEditSource] = useState("xtream");
  const [editForm, setEditForm] = useState(emptyForm);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const lookup = async (e) => {
    e?.preventDefault?.();
    setLookupError("");
    if (lookupMode === "key" && !lookupKey.trim()) {
      setLookupError("Enter your activation key.");
      return;
    }
    if (lookupMode === "mac" && lookupMac.replace(/[^0-9A-Fa-f]/g, "").length !== 12) {
      setLookupError("Enter a valid 12-character MAC address.");
      return;
    }
    setBusy(true);
    try {
      const body =
        lookupMode === "key"
          ? { activation_key: lookupKey.trim().toUpperCase() }
          : { mac_address: lookupMac };
      const res = await fetch(`${BACKEND}/api/trial/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setLookupError(data?.detail || "Look-up failed.");
        return;
      }
      setSession(data);
    } catch (err) {
      setLookupError(err?.message || "Network error.");
    } finally {
      setBusy(false);
    }
  };

  const refreshSession = async () => {
    if (!session?.activation_key) return;
    const res = await fetch(`${BACKEND}/api/trial/lookup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ activation_key: session.activation_key }),
    });
    if (res.ok) setSession(await res.json());
  };

  const openAdd = () => {
    setEditing({});
    setEditSource("xtream");
    setEditForm(emptyForm);
    setEditError("");
  };

  const openEdit = (p) => {
    // We can't reverse-engineer credentials from an Xtream URL reliably,
    // so editing always exposes the raw M3U/EPG URLs. Users who want to
    // change credentials should re-enter them via the Xtream tab.
    setEditing({ playlist_id: p.id });
    setEditSource("raw");
    setEditForm({
      ...emptyForm,
      name: p.name || "",
      m3u_url: p.m3u_url || "",
      epg_url: p.epg_url || "",
    });
    setEditError("");
  };

  const closeEdit = () => {
    setEditing(null);
    setEditError("");
    setEditForm(emptyForm);
    setEditSource("xtream");
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setEditError("");
    const name = editForm.name.trim();
    if (!name) {
      setEditError("Give your playlist a name.");
      return;
    }
    let m3u = "";
    let epg = "";
    if (editSource === "xtream") {
      if (!editForm.host.trim() || !editForm.username.trim()) {
        setEditError("Host and username are required for Xtream Codes.");
        return;
      }
      const built = buildXtreamUrls(editForm);
      m3u = built.m3u_url;
      epg = built.epg_url;
    } else {
      if (!editForm.m3u_url.trim()) {
        setEditError("Paste the M3U URL.");
        return;
      }
      m3u = editForm.m3u_url.trim();
      epg = editForm.epg_url.trim();
    }

    setEditBusy(true);
    try {
      if (editing.playlist_id) {
        // UPDATE
        const res = await fetch(`${BACKEND}/api/trial/update-playlist`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            activation_key: session.activation_key,
            playlist_id: editing.playlist_id,
            name,
            m3u_url: m3u,
            epg_url: epg || null,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          setEditError(data?.detail || "Update failed.");
          return;
        }
      } else {
        // ADD
        const res = await fetch(`${BACKEND}/api/trial/add-playlist`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            activation_key: session.activation_key,
            name,
            m3u_url: m3u,
            epg_url: epg || null,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          setEditError(data?.detail || "Could not add the playlist.");
          return;
        }
      }
      await refreshSession();
      closeEdit();
    } catch (err) {
      setEditError(err?.message || "Network error.");
    } finally {
      setEditBusy(false);
    }
  };

  const removePlaylist = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This can't be undone.`)) return;
    setDeletingId(p.id);
    try {
      const res = await fetch(`${BACKEND}/api/trial/delete-playlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          activation_key: session.activation_key,
          playlist_id: p.id,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data?.detail || "Delete failed.");
        return;
      }
      await refreshSession();
    } finally {
      setDeletingId(null);
    }
  };

  const canAdd = session && session.playlists.length < session.max_playlists && !session.is_expired;

  return (
    <div className="min-h-screen bg-black text-white antialiased" data-testid="playlist-page">
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="playlist-home-link">
            <img src="/za-logo-icon.png" alt="ZA Player Pro" className="h-9 w-9 rounded-lg" />
            <span className="text-lg font-bold tracking-tight">ZA Player Pro</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-emerald-400" data-testid="playlist-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden py-16 md:py-20">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-8 h-[400px] w-[800px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[140px]" />
        </div>
        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-emerald-400">
            Playlist manager
          </div>
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl" data-testid="playlist-title">
            Add, edit or renew your playlist
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-neutral-300">
            Enter your activation key or the MAC address you registered your app with, and
            we&apos;ll open your playlists for editing.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24">
        <div className="mx-auto max-w-3xl px-6">
          {!session ? (
            <form
              onSubmit={lookup}
              className="rounded-3xl border border-white/10 bg-neutral-950 p-8"
              data-testid="playlist-lookup-form"
            >
              <Tabs value={lookupMode} onValueChange={setLookupMode}>
                <TabsList className="grid w-full grid-cols-2 bg-neutral-900">
                  <TabsTrigger value="key" data-testid="lookup-tab-key">
                    <KeyRound className="mr-2 h-4 w-4" /> Activation key
                  </TabsTrigger>
                  <TabsTrigger value="mac" data-testid="lookup-tab-mac">
                    <ListVideo className="mr-2 h-4 w-4" /> MAC address
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="key" className="mt-6">
                  <Label className="text-xs text-neutral-400">Your activation key</Label>
                  <Input
                    value={lookupKey}
                    onChange={(e) => setLookupKey(e.target.value.toUpperCase())}
                    placeholder="ABCD1234EFGH"
                    className="border-white/10 bg-black font-mono text-lg tracking-widest text-white placeholder:text-neutral-600"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={40}
                    data-testid="lookup-key-input"
                  />
                </TabsContent>

                <TabsContent value="mac" className="mt-6">
                  <Label className="text-xs text-neutral-400">Device MAC address</Label>
                  <Input
                    value={lookupMac}
                    onChange={(e) => setLookupMac(formatMac(e.target.value))}
                    placeholder="AA:BB:CC:11:22:33"
                    className="border-white/10 bg-black text-lg tracking-widest text-white placeholder:text-neutral-600"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={17}
                    data-testid="lookup-mac-input"
                  />
                  <p className="mt-2 text-[11px] text-neutral-500">
                    Auto-formatted with colons. Only works if the MAC has claimed a trial.
                  </p>
                </TabsContent>
              </Tabs>

              {lookupError && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-100" data-testid="lookup-error">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-red-400" />
                  <span>{lookupError}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={busy}
                className="mt-6 w-full rounded-full bg-emerald-500 py-6 text-base font-semibold text-black hover:bg-emerald-400"
                data-testid="lookup-submit"
              >
                {busy ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Looking up…
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-5 w-5" /> Look up my playlists
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-6" data-testid="playlist-session">
              {/* Key summary */}
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-neutral-950 p-5">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-neutral-500">
                    Activation key
                  </div>
                  <div className="font-mono text-xl tracking-widest text-emerald-400" data-testid="session-key">
                    {session.activation_key}
                  </div>
                  {session.expires_at && (
                    <div className="mt-1 flex items-center gap-2 text-xs text-neutral-400">
                      <Clock className="h-3.5 w-3.5" />
                      {session.is_expired ? (
                        <span className="text-red-400">Expired on {new Date(session.expires_at).toLocaleDateString()}</span>
                      ) : (
                        <>Expires {new Date(session.expires_at).toLocaleDateString()} · {session.days_remaining} day(s) left</>
                      )}
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setSession(null)}
                  className="border-white/20 bg-white/5 text-white hover:bg-white/10"
                  data-testid="session-signout"
                >
                  Use another key
                </Button>
              </div>

              {/* Expired banner + renew */}
              {session.is_expired && (
                <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-5" data-testid="expired-banner">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-none text-red-400" />
                    <div className="flex-1">
                      <div className="font-semibold text-red-100">This activation has expired.</div>
                      <p className="mt-1 text-sm text-red-100/80">
                        Renew it to keep watching. Your playlist stays attached to your key — no need to re-enter anything.
                      </p>
                    </div>
                  </div>
                  <Link to="/activation">
                    <Button
                      className="mt-4 w-full rounded-full bg-emerald-500 py-6 text-base font-semibold text-black hover:bg-emerald-400"
                      data-testid="renew-btn"
                    >
                      <RefreshCw className="mr-2 h-5 w-5" /> Renew activation
                    </Button>
                  </Link>
                </div>
              )}

              {/* Playlist list */}
              <div className="rounded-2xl border border-white/10 bg-neutral-950 p-5">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-semibold text-white">
                    <ListVideo className="h-4 w-4 text-emerald-400" />
                    Your playlists ({session.playlists.length}/{session.max_playlists})
                  </div>
                  <Button
                    disabled={!canAdd}
                    onClick={openAdd}
                    className="rounded-full bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-40"
                    data-testid="add-playlist-btn"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add
                  </Button>
                </div>

                {session.playlists.length === 0 ? (
                  <div className="rounded-xl border border-white/5 bg-black/40 p-6 text-center text-sm text-neutral-500" data-testid="no-playlists">
                    No playlists yet. Add one to activate this device.
                  </div>
                ) : (
                  <ul className="space-y-3" data-testid="playlist-list">
                    {session.playlists.map((p) => (
                      <li
                        key={p.id}
                        className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-black/40 p-4"
                        data-testid={`playlist-item-${p.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-white">{p.name}</div>
                          <div className="truncate font-mono text-[11px] text-neutral-500">{p.m3u_url}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEdit(p)}
                            disabled={session.is_expired}
                            className="border-white/20 bg-white/5 text-white hover:bg-white/10"
                            data-testid={`playlist-edit-${p.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removePlaylist(p)}
                            disabled={deletingId === p.id}
                            className="border-red-500/30 bg-red-500/5 text-red-300 hover:bg-red-500/10"
                            data-testid={`playlist-delete-${p.id}`}
                          >
                            {deletingId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {!canAdd && !session.is_expired && (
                  <p className="mt-3 text-[11px] text-neutral-500">
                    You&apos;ve reached the playlist cap for this key
                    {session.is_trial ? " (trial keys are limited to 1)" : ""}.
                  </p>
                )}
              </div>

              {/* Success/hint */}
              {!session.is_expired && session.playlists.length > 0 && (
                <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5 text-sm text-emerald-100/90" data-testid="ready-hint">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-emerald-400" />
                    <div>
                      Install ZA Player Pro on your device and enter{" "}
                      <span className="font-mono text-emerald-300">{session.activation_key}</span>{" "}
                      — your playlist loads automatically.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} ZA Player Pro. Player-only application. No content included.
      </footer>

      {/* Add / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="max-h-[92vh] overflow-y-auto border-white/10 bg-neutral-900 text-white sm:max-w-lg" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListVideo className="h-5 w-5 text-emerald-400" />
              {editing?.playlist_id ? "Edit playlist" : "Add playlist"}
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              {editing?.playlist_id
                ? "Update this playlist. The activation key stays the same."
                : `This playlist will be attached to ${session?.activation_key || "your key"}.`}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submitEdit} className="space-y-4 py-2" data-testid="edit-form">
            <div>
              <Label className="text-xs text-neutral-400">Playlist name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="My provider"
                className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-600"
                maxLength={80}
                data-testid="edit-name"
              />
            </div>

            <Tabs value={editSource} onValueChange={setEditSource}>
              <TabsList className="grid w-full grid-cols-2 bg-neutral-950">
                <TabsTrigger value="xtream" data-testid="edit-tab-xtream">Xtream Codes</TabsTrigger>
                <TabsTrigger value="raw" data-testid="edit-tab-raw">Raw M3U URL</TabsTrigger>
              </TabsList>

              <TabsContent value="xtream" className="mt-4 space-y-3">
                <div>
                  <Label className="text-xs text-neutral-400">Host</Label>
                  <Input
                    value={editForm.host}
                    onChange={(e) => setEditForm({ ...editForm, host: e.target.value })}
                    placeholder="line.myprovider.com"
                    className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-600"
                    autoCapitalize="off" autoCorrect="off"
                    data-testid="edit-host"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <Label className="text-xs text-neutral-400">Port</Label>
                    <Input
                      value={editForm.port}
                      onChange={(e) => setEditForm({ ...editForm, port: e.target.value })}
                      placeholder="80"
                      className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-600"
                      inputMode="numeric"
                      data-testid="edit-port"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-neutral-400">Username</Label>
                    <Input
                      value={editForm.username}
                      onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                      placeholder="your-username"
                      className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-600"
                      autoCapitalize="off" autoCorrect="off"
                      data-testid="edit-username"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-neutral-400">Password</Label>
                  <Input
                    type="password"
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                    placeholder="••••••••"
                    className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-600"
                    data-testid="edit-password"
                  />
                </div>
              </TabsContent>

              <TabsContent value="raw" className="mt-4 space-y-3">
                <div>
                  <Label className="text-xs text-neutral-400">M3U URL</Label>
                  <Input
                    value={editForm.m3u_url}
                    onChange={(e) => setEditForm({ ...editForm, m3u_url: e.target.value })}
                    placeholder="http://host/get.php?username=…&password=…"
                    className="border-white/10 bg-neutral-950 font-mono text-xs text-white placeholder:text-neutral-600"
                    autoCapitalize="off" autoCorrect="off" spellCheck={false}
                    data-testid="edit-m3u-url"
                  />
                </div>
                <div>
                  <Label className="text-xs text-neutral-400">EPG URL <span className="text-neutral-600">(optional)</span></Label>
                  <Input
                    value={editForm.epg_url}
                    onChange={(e) => setEditForm({ ...editForm, epg_url: e.target.value })}
                    placeholder="http://host/xmltv.php?…"
                    className="border-white/10 bg-neutral-950 font-mono text-xs text-white placeholder:text-neutral-600"
                    autoCapitalize="off" autoCorrect="off" spellCheck={false}
                    data-testid="edit-epg-url"
                  />
                </div>
              </TabsContent>
            </Tabs>

            {editError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-100" data-testid="edit-error">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-red-400" />
                <span>{editError}</span>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeEdit}
                className="border-white/20 bg-white/5 text-white hover:bg-white/10"
                data-testid="edit-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={editBusy}
                className="bg-emerald-500 text-black hover:bg-emerald-400"
                data-testid="edit-submit"
              >
                {editBusy ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
                ) : (
                  <><CheckCircle2 className="mr-2 h-4 w-4" /> {editing?.playlist_id ? "Save changes" : "Add playlist"}</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
